import type { NutritionEUColumn } from "./dish-nutrition";

type FractionDigits = 0 | 1 | 2;

/** How to show a value: `value` is unrounded, for the formatter to round. */
export type NutritionRounding =
  | { kind: "value"; value: number; maximumFractionDigits: FractionDigits }
  | { kind: "lessThan"; limit: number };

type Band = {
  digits: (value: number) => FractionDigits;
  /** Above 0 and at most `upTo`, the value is declared as less than `limit`. */
  trace: { upTo: number; limit: number } | null;
};

const ENERGY: Band = { digits: () => 0, trace: null };

const GRAMS: Band = {
  digits: (value) => (value >= 10 ? 0 : 1),
  trace: { upTo: 0.5, limit: 0.5 },
};

const FATTY_ACIDS: Band = {
  digits: (value) => (value >= 10 ? 0 : 1),
  trace: { upTo: 0.1, limit: 0.1 },
};

const SALT: Band = {
  digits: (value) => (value >= 1 ? 1 : 2),
  trace: { upTo: 0.0125, limit: 0.01 },
};

const BANDS: Record<keyof NutritionEUColumn, Band> = {
  energyKj: ENERGY,
  energyKcal: ENERGY,
  fat: GRAMS,
  saturates: FATTY_ACIDS,
  monounsaturates: FATTY_ACIDS,
  polyunsaturates: FATTY_ACIDS,
  carbohydrate: GRAMS,
  sugars: GRAMS,
  polyols: GRAMS,
  starch: GRAMS,
  fibre: GRAMS,
  protein: GRAMS,
  salt: SALT,
};

/**
 * The rounding band for a value in the nutrition table, as the European
 * Commission's December 2012 guidance on tolerances sets out (table 4),
 * chosen on the unrounded value.
 */
export const nutritionRounding = (
  nutrient: keyof NutritionEUColumn,
  value: number | null
): NutritionRounding | null => {
  if (value === null) return null;
  if (value === 0) return { kind: "value", value: 0, maximumFractionDigits: 0 };
  const { digits, trace } = BANDS[nutrient];
  if (trace !== null && value <= trace.upTo) {
    return { kind: "lessThan", limit: trace.limit };
  }
  return { kind: "value", value, maximumFractionDigits: digits(value) };
};
